import type { Directive, DirectiveBinding } from 'vue';

type BreakpointKey = 'Small' | 'Medium' | 'Large' | 'ExtraLarge';

type SkeletonSize = {
	width: string;
	height: string;
};

type SkeletonSizes = Partial<Record<BreakpointKey, SkeletonSize>>;

type SkeletonMargins = Partial<Record<BreakpointKey, string>>;

export type SkeletonBinding = {
	loading: boolean;
	sizes?: SkeletonSizes;
	margins?: SkeletonMargins;
};

const defaultSize: SkeletonSize = { width: '2.5rem', height: '2.5rem' };
const defaultMargin = '0';

const applySkeleton = (el: HTMLElement, value?: SkeletonBinding): void => {
	const loading = !!value?.loading;
	const sizes = value?.sizes ?? {};
	const margins = value?.margins ?? {};

	el.classList.add('SkeletonBlock');

	// --- sizes ---
	const sm = sizes.Small ?? defaultSize;
	const md = sizes.Medium ?? sm;
	const lg = sizes.Large ?? md;
	const xl = sizes.ExtraLarge ?? lg;

	el.style.setProperty('--skel-w-sm', sm.width);
	el.style.setProperty('--skel-h-sm', sm.height);
	el.style.setProperty('--skel-w-md', md.width);
	el.style.setProperty('--skel-h-md', md.height);
	el.style.setProperty('--skel-w-lg', lg.width);
	el.style.setProperty('--skel-h-lg', lg.height);
	el.style.setProperty('--skel-w-xl', xl.width);
	el.style.setProperty('--skel-h-xl', xl.height);

	// --- margins ---
	const mSm = margins.Small ?? defaultMargin;
	const mMd = margins.Medium ?? mSm;
	const mLg = margins.Large ?? mMd;
	const mXl = margins.ExtraLarge ?? mLg;

	el.style.setProperty('--skel-m-sm', mSm);
	el.style.setProperty('--skel-m-md', mMd);
	el.style.setProperty('--skel-m-lg', mLg);
	el.style.setProperty('--skel-m-xl', mXl);

	if (loading) {
		el.classList.add('loading');
	} else {
		el.classList.remove('loading');
	}
};

export const skeletonDirective: Directive<HTMLElement, SkeletonBinding> = {
	mounted: (el, binding: DirectiveBinding<SkeletonBinding>) => {
		applySkeleton(el, binding.value);
	},
	updated: (el, binding: DirectiveBinding<SkeletonBinding>) => {
		applySkeleton(el, binding.value);
	},
};
